import React, {useMemo} from 'react';
import {Button} from '../../components';
import {isBrowser} from '../../utils/isBrowser';
import {useScreenSize} from '../../utils/useScreenSize';
import {Page} from '../../types';

import aotd from '../../images/app-ot-day-light.png';

interface IDownloadBar {
  ctaCopy: string;
  title: string;
  linkTo: Page;
}

export const DownloadBar = ({ctaCopy, title, linkTo}: IDownloadBar) => {
  const [scrollPosition, setScrollPosition] = React.useState(
    isBrowser ? window.scrollY : 0,
  );
  const scrollPositionRef = React.useRef(scrollPosition);
  const [styling, setStyling] = React.useState('hidden');

  const onHandleScroll = React.useCallback(() => {
    setScrollPosition(window.scrollY);
  }, []);

  React.useEffect(() => {
    if (isBrowser) {
      window.addEventListener('scroll', onHandleScroll);
      return () => {
        window.removeEventListener('scroll', onHandleScroll);
      };
    }
  }, [onHandleScroll]);

  const pageHeight = isBrowser
    ? Math.max(
        document.body.getBoundingClientRect().height,
        document.documentElement.getBoundingClientRect().height,
      )
    : 0;

  const screenSize = useScreenSize();

  const isNearBottom = useMemo(() => {
    // this is to prevent the bounceback seen on mobile safari removing the download bar when the user scrolls to the bottom of the page, it provides a "buffer"
    //  as a side effect, it does mean the download bar stays visible when scrolling up from the bottom for for 700 pixels
    if (screenSize === 'small') {
      return scrollPosition > pageHeight - 700;
    }
  }, [scrollPosition, pageHeight, screenSize]);

  React.useEffect(() => {
    if (scrollPosition !== scrollPositionRef.current) {
      if (isNearBottom) {
        setStyling('');
      } else if (scrollPosition > scrollPositionRef.current) {
        if (scrollPosition > 10) {
          setStyling('animate-fadeIn');
        }
      } else {
        setStyling('animate-fadeOut');
      }
    }
    scrollPositionRef.current = scrollPosition;
  }, [scrollPosition, isNearBottom]);

  return (
    <div className={`fixed bottom-0 bg-primaryred w-full z-40 ${styling}`}>
      <div className="flex justify-between md:justify-center items-center gap-8 mx-4">
        <img
          src={aotd}
          alt="app of the day logo"
          className="h-12 hidden md:block"
        />
        <p className="text-smallbody font-semibold text-white">{title}</p>
        <div className="min-w-fit py-3">
          <Button label={ctaCopy} linkTo={linkTo} type="secondary" />
        </div>
      </div>
    </div>
  );
};
